import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Footprints, ScrollText, Handshake, Link2, CheckCircle2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICON = { partner_attempt: Footprints, server_link_attempt: Link2, partner_affidavit: ScrollText, job_shared: Handshake, job_ack: CheckCircle2, affidavit_signed: ScrollText, partner_note: MessageSquare };
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export function NotificationBell({ className = "" }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const poll = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { poll(); const t = setInterval(poll, 30000); return () => clearInterval(t); }, []);

  const openItem = async (n) => {
    if (!n.read) api.post(`/notifications/${n.id}/read`).then(poll).catch(() => {});
    setOpen(false);
    if (n.job_id) navigate(`/jobs/${n.job_id}`);
  };
  const readAll = () => api.post("/notifications/read-all").then(poll).catch(() => {});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`relative p-1 text-white/80 hover:text-white ${className}`} title="Notifications" data-testid="notification-bell">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-4 text-center" data-testid="notification-unread-count">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
          <div className="text-sm font-semibold">Partner activity</div>
          {data.unread > 0 && <button type="button" className="sm-link text-xs" onClick={readAll} data-testid="notification-read-all">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {data.items.length === 0 ? <div className="px-3 py-6 text-sm text-slate-400 text-center" data-testid="notification-empty">No partner activity yet.</div>
            : data.items.map((n) => { const I = ICON[n.kind] || Bell; return (
              <button key={n.id} type="button" onClick={() => openItem(n)} className={`w-full text-left px-3 py-2.5 flex gap-2.5 hover:bg-slate-50 ${n.read ? "" : "bg-sky-50/70"}`} data-testid={`notification-item-${n.id}`}>
                <I className={`h-4 w-4 mt-0.5 shrink-0 ${n.read ? "text-slate-400" : "text-[#1f5fa8]"}`} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm leading-snug ${n.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>{n.title}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{ago(n.created_at)}</div>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-[#1f5fa8] mt-1.5 shrink-0" />}
              </button>
            ); })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
